import { AgentConfig } from "../types";
import { freezeAgentConfig } from '../FreezeDiagnostic';
import { AutonomousIntelligence } from '@/services/intelligence/AutonomousIntelligence';
import type { Schema } from '@/shared/types/ai.dto';
import { SocialTools } from '../tools/SocialTools';
import { UniversalTools } from '../tools/UniversalTools';
import systemPrompt from '@agents/social/prompt.md?raw';
import { buildDomainRetrievalTools, buildDomainRetrievalDeclarations } from '../tools/DomainTools';



const socialRetrievalConfig = {
    'scheduled_posts': { path: 'scheduled_posts', requiresUserIdFilter: true },
    'posts': { path: 'posts', requiresUserIdFilter: true }
};
const socialRetrievalTools = buildDomainRetrievalTools('Social', socialRetrievalConfig);
const socialRetrievalDeclarations = buildDomainRetrievalDeclarations('Social', socialRetrievalConfig);

export const SocialAgent: AgentConfig = {
    id: 'social',
    name: 'Social Media Director',
    description: 'Manages social media presence, trends, and community engagement.',
    color: 'bg-sky-400',
    category: 'department',
    systemPrompt: systemPrompt,
    get functions() {
        return {
            ...socialRetrievalTools,
            analyze_trends: async (args: { topic: string; observedSignals?: string[] }) => {
                if (!args.observedSignals?.length) return { success: false, error: 'Observed platform signals are required; trend metrics will not be fabricated.' };
                const prompt = `Analyze these observed social signals for "${args.topic}": ${args.observedSignals.join(' | ')}. Return JSON with trend_score, sentiment, keywords, source labels, and summary. Never infer an audio trend without an observed audio identifier.`;
                try {
                    const response = await AutonomousIntelligence.generateStructuredData(prompt, { type: 'object' } as Schema, { maxOutputTokens: 8192, temperature: 1.0 });
                    return { success: true, data: response };
                } catch (e: unknown) {
                    return { success: false, error: (e as Error).message };
                }
            },
            generate_social_post: SocialTools.generate_social_post,
            generate_content_calendar: async (args: { releaseDate: string, campaignTitle: string, durationWeeks: number }) => {
                const prompt = `Generate a long-term social media content calendar for a music release.
                Campaign: ${args.campaignTitle}
                Release Date: ${args.releaseDate}
                Duration: ${args.durationWeeks} weeks
                
                Include:
                - Pre-release (Hype/Teasers)
                - Release Day (Launch/Direct links)
                - Post-release (UGC/Music Video/Remix)
                - Platform-specific frequency (TikTok daily, IG 3x/week, etc.)`;

                try {
                    const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                    return { success: true, data: { calendar: response } };
                } catch (e: unknown) {
                    return { success: false, error: (e as Error).message };
                }
            },
            schedule_post_execution: SocialTools.schedule_social_post,
            draft_advanced_thread: async (args: { topic: string, platform: string, threadLength: number }) => {
                const prompt = `Draft a compelling ${args.threadLength}-part advanced thread for ${args.platform} about ${args.topic}. Make each part flow smoothly into the next, using hooks and cliffhangers where appropriate. Return an array of strings.`;
                try {
                    const response = await AutonomousIntelligence.generateStructuredData(prompt, { type: 'array', items: { type: 'string' } } as Schema, { maxOutputTokens: 8192, temperature: 1.0 });
                    return { success: true, data: { thread: response } };
                } catch (e: unknown) {
                    return { success: false, error: (e as Error).message };
                }
            },
            web_extract: UniversalTools.web_extract,
            indii_image_gen: UniversalTools.indii_image_gen,
            credential_vault: UniversalTools.credential_vault,
            analyze_sentiment: SocialTools.analyze_sentiment,
            multi_platform_autopost: SocialTools.multi_platform_autopost,
            dispatch_community_webhook: SocialTools.dispatch_community_webhook,
            analyze_engagement_rate: async (args: { platform: string, timePeriod: string }) => {
                const prompt = `Analyze engagement rate for ${args.platform} over the last ${args.timePeriod}. Calculate the total engagement divided by followers or impressions, and provide actionable tips to increase engagement.`;
                try {
                    const response = await AutonomousIntelligence.generateText(prompt, { maxOutputTokens: 8192, temperature: 1.0 });
                    return { success: true, data: { analysis: response } };
                } catch (e: unknown) {
                    return { success: false, error: (e as Error).message };
                }
            },
        } as Record<string, import('@/services/agent/types').AnyToolFunction>;
    },
    authorizedTools: ['list_domain_records', 'generate_content_calendar', 'schedule_post_execution', 'generate_social_post', 'analyze_trends', 'web_extract', 'indii_image_gen', 'credential_vault', 'draft_advanced_thread', 'analyze_sentiment', 'multi_platform_autopost', 'dispatch_community_webhook', 'analyze_engagement_rate'],
    tools: [{
        functionDeclarations: [
            ...socialRetrievalDeclarations,
            {
                name: "generate_content_calendar",
                description: "Generate a multi-week content calendar for a music release.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        releaseDate: { type: "STRING", description: "YYYY-MM-DD" },
                        campaignTitle: { type: "STRING" },
                        durationWeeks: { type: "NUMBER" }
                    },
                    required: ["releaseDate", "campaignTitle"]
                }
            },
            {
                name: "schedule_post_execution",
                description: "Schedule a post for long-term execution on a specific platform.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        platform: { type: "STRING" },
                        content: { type: "STRING" },
                        scheduledTime: { type: "STRING", description: "ISO 8601 timestamp" },
                        instagramPayload: { type: "OBJECT", description: "Required for Instagram: surface, exact dimensions, caption, hashtags, and applicable duration intent." }
                    },
                    required: ["platform", "content", "scheduledTime"]
                }
            },
            {
                name: "generate_social_post",
                description: "Generate reviewable social media draft copy without saving or posting it.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        platform: { type: "STRING", description: "Platform (Twitter, LinkedIn, Instagram, etc)." },
                        topic: { type: "STRING", description: "What the post is about." },
                        tone: { type: "STRING", description: "Desired tone." }
                    },
                    required: ["platform", "topic"]
                }
            },
            {
                name: "analyze_trends",
                description: "Analyze current trends for a topic.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        topic: { type: "STRING", description: "Topic to analyze." },
                        observedSignals: { type: "ARRAY", items: { type: "STRING" }, description: "Observed platform signals with source metadata." }
                    },
                    required: ["topic"]
                }
            },
            {
                name: "web_extract",
                description: "Read bounded text from a public web page. This tool cannot log in, click, type, or submit forms.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        url: { type: "STRING", description: "Public HTTP(S) URL to read." }
                    },
                    required: ["url"]
                }
            },
            {
                name: "indii_image_gen",
                description: "Generate memes, quote cards, or social assets.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        prompt: { type: "STRING" },
                        aspect_ratio: { type: "STRING" }
                    },
                    required: ["prompt"]
                }
            },
            {
                name: "credential_vault",
                description: "Retrieve social media login credentials.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        action: { type: "STRING", description: "retrieve" },
                        service: { type: "STRING", description: "Service name (e.g. TikTok)" }
                    },
                    required: ["action", "service"]
                }
            },
            {
                name: "draft_advanced_thread",
                description: "Draft an advanced multi-part thread for a social platform.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        topic: { type: "STRING" },
                        platform: { type: "STRING" },
                        threadLength: { type: "NUMBER" }
                    },
                    required: ["topic", "platform", "threadLength"]
                }
            },
            {
                name: "analyze_sentiment",
                description: "Crawls recent comments/mentions across linked socials (X/IG) and provides a sentiment and trend report.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        platform: { type: "STRING", enum: ["All", "X", "Instagram", "TikTok"], description: "The platform to analyze." },
                        timeframe: { type: "STRING", enum: ["7d", "14d", "30d"], description: "How far back to analyze." }
                    },
                    required: ["platform", "timeframe"]
                }
            },
            {
                name: "multi_platform_autopost",
                description: "Direct API integration tool to automatically queue and post a single video to multiple short-form platforms (TikTok, YouTube Shorts, IG Reels) natively.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        videoUrl: { type: "STRING", description: "Public URL of the 9:16 short form video to upload." },
                        caption: { type: "STRING", description: "The caption to include across all platforms." },
                        hashtags: { type: "ARRAY", items: { type: "STRING" }, description: "List of hashtags to append." },
                        width: { type: "NUMBER", description: "Required exact media width for IG Reels (1080)." },
                        height: { type: "NUMBER", description: "Required exact media height for IG Reels (1920)." },
                        durationSeconds: { type: "NUMBER", description: "Required Reel duration." },
                        reelAudienceIntent: { type: "STRING", enum: ["discovery", "nurture"], description: "Discovery is under 15 seconds; nurture is over 30 seconds." },
                        platforms: {
                            type: "ARRAY",
                            items: { type: "STRING", enum: ["TikTok", "YouTube Shorts", "IG Reels"] },
                            description: "Which platforms to push to simultaneously."
                        }
                    },
                    required: ["videoUrl", "caption", "platforms"]
                }
            },
            {
                name: "dispatch_community_webhook",
                description: "Dispatches an automated announcement (with rich embeds) into an artist's Discord or Telegram community server via webhook.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        platform: { type: "STRING", enum: ["Discord", "Telegram"], description: "The community platform." },
                        webhookUrl: { type: "STRING", description: "The secure webhook URL." },
                        messageContent: { type: "STRING", description: "The main text of the announcement." },
                        embedTitle: { type: "STRING", description: "Title of the rich embed (e.g., 'New Drop!')." },
                        embedImageUrl: { type: "STRING", description: "URL of the cover art or promo image." },
                        embedLink: { type: "STRING", description: "Call to action link (e.g., pre-save link)." }
                    },
                    required: ["platform", "webhookUrl", "messageContent"]
                }
            },
            {
                name: "analyze_engagement_rate",
                description: "Analyze the engagement rate across social platforms.",
                parameters: {
                    type: "OBJECT",
                    properties: {
                        platform: { type: "STRING", description: "The platform to analyze." },
                        timePeriod: { type: "STRING", description: "The time period to analyze (e.g., '7d', '30d')." }
                    },
                    required: ["platform", "timePeriod"]
                }
            }
        ]
    }]
};

// Freeze the schema to prevent cross-test contamination
freezeAgentConfig(SocialAgent);
